
import { useState, useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { Plus, Shield, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { CreateLevelDialog } from "./CreateLevelDialog";
import { LevelDetailView } from "./LevelDetailView";
import { useQuery as useConvexQuery } from "convex/react";
import { api } from "../../api";
import { format } from "date-fns";
import { Level } from "@/hooks/useLevels";

interface LevelCounts {
  devices: number;
}

export const LevelsSection = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [selectedLevel, setSelectedLevel] = useState<Level | null>(null);
  const [isFullScreenView, setIsFullScreenView] = useState(false);
  const [activeTab, setActiveTab] = useState("levels");

  // Fetch levels using Convex
  const levelsData = useConvexQuery(api.levels.list.all, {
    paginationOpts: {
      cursor: null,
      numItems: 100
    }
  });

  // For device counting, we'll need to use a different approach since the basic device list
  // doesn't include level information. For now, we'll set counts to 0 and add a TODO.
  const allLevels = levelsData?.page || [];
  const isLoading = levelsData === undefined;

  // Filter levels based on active tab
  const levels = useMemo(() => {
    if (activeTab === "templates") {
      return allLevels.filter(level => (level as any).template === true);
    }
    return allLevels.filter(level => !(level as any).template);
  }, [allLevels, activeTab]);

  // Restore active tab from URL params
  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab && ['levels', 'templates'].includes(tab)) {
      setActiveTab(tab);
    }
  }, [searchParams]);

  // Save active tab to URL params
  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set('tab', tab);
    setSearchParams(newSearchParams);
  };

  // Calculate device counts per level - currently set to 0 since device API doesn't include level info
  const levelCounts = useMemo(() => {
    if (!levels.length) {
      return {};
    }

    const counts: Record<string, LevelCounts> = {};
    
    // Initialize all levels with 0 devices
    // TODO: Implement proper device counting when level information is available in device API
    levels.forEach(level => {
      counts[level.name] = { devices: 0 };
    });

    return counts;
  }, [levels]);

  // Restore selected level from URL params
  useEffect(() => {
    const levelId = searchParams.get('levelId');
    if (levelId && levels) {
      const level = levels.find(l => l._id === levelId);
      if (level) {
        setSelectedLevel({
          ...level,
          _id: level._id,
          _creationTime: Date.now()
        } as any);
        setIsFullScreenView(true);
      }
    }
  }, [levels, searchParams]);

  const handleLevelSelect = (level: Level) => {
    setSelectedLevel(level);
    setIsFullScreenView(true);
    // Save level selection to URL - use Convex ID for proper lookup
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set('levelId', level._id);
    setSearchParams(newSearchParams);
  };

  const handleBackToList = () => {
    setSelectedLevel(null);
    setIsFullScreenView(false);
    // Remove level selection from URL
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.delete('levelId');
    newSearchParams.delete('tab');
    newSearchParams.delete('appsSection');
    setSearchParams(newSearchParams);
  };

  const getStatusVariant = (isPrivate: boolean) => {
    return isPrivate ? 'secondary' : 'default';
  };

  const getStatusLabel = (isPrivate: boolean) => {
    return isPrivate ? 'Private' : 'Public';
  };

  // Show full-screen level detail view
  if (isFullScreenView && selectedLevel) {
    return <LevelDetailView level={selectedLevel} onBack={handleBackToList} />;
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        <span className="ml-2 text-gray-600">Loading levels...</span>
      </div>
    );
  }

  // Create a reusable component for rendering the level/template list
  const renderLevelsList = (items: any[], emptyMessage: string, emptyDescription: string) => {
    if (items.length === 0) {
      return (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Shield className="w-12 h-12 text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">{emptyMessage}</h3>
            <p className="text-gray-500 text-center mb-6 max-w-md">
              {emptyDescription}
            </p>
            <Button onClick={() => setIsCreateDialogOpen(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Create Your First {activeTab === "templates" ? "Template" : "Level"}
            </Button>
          </CardContent>
        </Card>
      );
    }

    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="overflow-x-auto">
          <Table className="min-w-[600px]">
            <TableHeader>
              <TableRow>
                <TableHead className="w-[200px]">Name</TableHead>
                <TableHead className="w-[100px]">Status</TableHead>
                <TableHead className="w-[120px]">Created</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map(level => {
                const createdDate = "Recently"; // _creationTime doesn't exist in API
                
                return (
                  <TableRow 
                    key={level.name} 
                    className="cursor-pointer hover:bg-muted/50" 
                    onClick={() => handleLevelSelect({
                      ...level,
                      _id: level._id, // Use the actual ID, not the name
                      _creationTime: Date.now()
                    } as any)}
                  >
                    <TableCell className="w-[200px]">
                      <div>
                        <div className="font-medium">{level.name}</div>
                        {level.description && (
                          <div className="text-sm text-muted-foreground mt-1">
                            {level.description}
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="w-[100px]">
                      <Badge variant={getStatusVariant(level.private)}>
                        {getStatusLabel(level.private)}
                      </Badge>
                    </TableCell>
                    <TableCell className="w-[120px] text-sm text-muted-foreground">
                      {createdDate}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6 px-6">
      <div className="flex justify-start items-center">
        <Button onClick={() => setIsCreateDialogOpen(true)}>
          <Plus className="w-4 h-4 mr-2" />
          Create Level
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="levels">Levels</TabsTrigger>
          <TabsTrigger value="templates">Templates</TabsTrigger>
        </TabsList>
        
        <TabsContent value="levels" className="space-y-4">
          {renderLevelsList(
            levels,
            "No levels created yet",
            "Create your first level to start organizing devices with custom restrictions, apps, and network rules."
          )}
        </TabsContent>
        
        <TabsContent value="templates" className="space-y-4">
          {renderLevelsList(
            levels,
            "No templates created yet",
            "Create your first template to use as a starting point for new levels with predefined configurations."
          )}
        </TabsContent>
      </Tabs>

      <CreateLevelDialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen} />
    </div>
  );
};

export default LevelsSection;
